import { useEffect, useState } from "react";
import { api, ApiError, type Workspace } from "../lib/api";
import { useAppContext } from "../hooks/useAppContext";
import { Button } from "./ui";

/**
 * Workspace (+ optionally guild) selector shared by the Phase 3 pages. The
 * selection lives in in-memory context only. The control API exposes no
 * "list connected guilds" endpoint, so the guild is entered by its ODIN guild
 * id (shown after connecting a server during onboarding) — never guessed.
 */
export function ContextBar({ requireGuild = false }: { requireGuild?: boolean }): JSX.Element {
  const ctx = useAppContext();
  const [workspaces, setWorkspaces] = useState<Workspace[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [guildDraft, setGuildDraft] = useState("");

  useEffect(() => {
    let active = true;
    api
      .listWorkspaces()
      .then((r) => active && setWorkspaces(r.workspaces))
      .catch((e) => active && setError(e instanceof ApiError ? e.message : "Could not load workspaces"));
    return () => {
      active = false;
    };
  }, []);

  return (
    <div className="context-bar">
      <label className="field">
        <span className="field__label">Workspace</span>
        <select
          className="field__input"
          value={ctx.tenantId ?? ""}
          onChange={(e) => {
            const w = workspaces?.find((x) => x.id === e.target.value);
            if (w) ctx.setWorkspace(w.id, w.name);
          }}
        >
          <option value="" disabled>
            {workspaces ? "Select a workspace…" : "Loading…"}
          </option>
          {(workspaces ?? []).map((w) => (
            <option key={w.id} value={w.id}>
              {w.name}
            </option>
          ))}
        </select>
      </label>

      {requireGuild ? (
        <div className="field">
          <span className="field__label">ODIN server (guild) id</span>
          <div className="row" style={{ gap: "0.5rem" }}>
            <input
              className="field__input"
              placeholder="guild id from onboarding"
              value={ctx.guildId ?? guildDraft}
              onChange={(e) => {
                ctx.clearGuild();
                setGuildDraft(e.target.value);
              }}
              aria-label="ODIN server guild id"
            />
            <Button
              variant="secondary"
              disabled={guildDraft.trim().length < 1 && !ctx.guildId}
              onClick={() => {
                const id = (ctx.guildId ?? guildDraft).trim();
                if (id) ctx.setGuild(id, id);
              }}
            >
              Use server
            </Button>
          </div>
        </div>
      ) : null}

      {error ? (
        <span className="status status--danger" role="status">
          <span className="status__dot" aria-hidden="true" />
          {error}
        </span>
      ) : null}
    </div>
  );
}
